// 9fbef606107a605d69c0edbcd8029e5d
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import React, { useState, useEffect } from 'react';
import { getAPIConfig, getIconPath } from '@tcp/core/src/utils';
import RichText from '@tcp/core/src/components/common/atoms/RichText';
import {
  formatPhoneNumber,
  validatePhoneNumber,
} from '@tcp/core/src/utils/formValidation/phoneNumber';
import {
  Row,
  Container,
  HeadingRow,
  AlertImage,
  FieldWrapper,
  HeadingLine1,
  HeadingLine2,
  SubmitButton,
  DisclaimerRow,
  FormContainer,
  LabelContainer,
  MessageSection,
  VisuallyHidden,
  SubmissionText,
  TabToJoinButton,
  PhoneNumberInput,
  TabToJoinContainer,
} from '../styles/SubscriptionSMS.style';

const SMS_SUBSCRIPTION_STATUS = {
  MDN_ALREADY_SUBSCRIBED: 'MdnAlreadySubscribed',
  MDN_CONFIRMATION_BLOCK: 'MdnHasConfirmationBlock',
};
const isMdnSubscribedOrBlocked = (subscription) => {
  return (
    subscription?.data?.status === SMS_SUBSCRIPTION_STATUS.MDN_ALREADY_SUBSCRIBED ||
    subscription?.data?.status === SMS_SUBSCRIPTION_STATUS.MDN_CONFIRMATION_BLOCK
  );
};

const submissionMessage = ({
  showSuccess,
  isErrorState,
  submissionLabel,
  isSubscriptionError,
  subscribedErrorLabel,
  validationErrorLabel,
}) => {
  return (
    <>
      {!showSuccess && isErrorState && (
        <MessageSection>
          <AlertImage
            aria-hidden={true}
            alt="warning-icon"
            className="submission-error-icon"
            src={getIconPath('circle-alert-fill')}
          />
          <SubmissionText>
            {isSubscriptionError ? subscribedErrorLabel : validationErrorLabel}
          </SubmissionText>
        </MessageSection>
      )}
      {showSuccess && (
        <MessageSection>
          <AlertImage
            aria-hidden={true}
            alt="success-icon"
            className="submission-success-icon"
            src={getIconPath('circle-check-fill-green')}
          />
          <SubmissionText isSubmissionSuccess>{submissionLabel}</SubmissionText>
        </MessageSection>
      )}
    </>
  );
};

const getTranslatedPage = (language) => language !== 'en';
const checkPlaceHolder = (isFocused, phoneNumber) => !isFocused && !phoneNumber;
const checkDisclaimer = (isHidden, smsDisclaimerVal) => !isHidden && !isEmpty(smsDisclaimerVal);

const getIsHidden = (hideDisclaimer) => {
  const { isHidden = false } = hideDisclaimer || {};
  return isHidden;
};

const SubscriptionSMSView = ({
  heading1,
  heading2,
  smsTemplate,
  submitTitle,
  classNameVal,
  tabToJoinCTA,
  smsDisclaimer,
  hideDisclaimer,
  submissionLabel,
  textColorOverride,
  subscriptionStatus,
  smsPlaceHolderText,
  validationErrorLabel,
  subscribedErrorLabel,
  subscriptionSmsSubmit,
}) => {
  const { language } = getAPIConfig();
  const isHidden = getIsHidden(hideDisclaimer);
  const isTranslatedPage = getTranslatedPage(language);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const [isSubscriptionError, setIsSubscriptionError] = useState(false);
  const showDisclaimer = checkDisclaimer(isHidden, smsDisclaimer?.richText);
  const shouldShowPlaceholder = checkPlaceHolder(isFocused, phoneNumber);

  const handleSMSChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setCanSubmit(true);
    setShowSuccess(false);
    setPhoneNumber(formatted);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = { sms: phoneNumber.trim() };

    const isValidNumber = validatePhoneNumber(formData.sms);

    if (isValidNumber) {
      subscriptionSmsSubmit({
        hpSubscriptionSms: formData.sms,
        component: 'hp_sms_module',
      });
    }
    if (!isValidNumber) {
      setCanSubmit(false);
      setShowSuccess(false);
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!isEmpty(subscriptionStatus)) {
      const isBlockedOrSubscribed = isMdnSubscribedOrBlocked(subscriptionStatus);
      setCanSubmit(false);
      setIsSubscriptionError(isBlockedOrSubscribed);
      setShowSuccess(!isBlockedOrSubscribed);
      if (!isBlockedOrSubscribed) {
        setPhoneNumber('');
      }
    }
  }, [subscriptionStatus]);

  const isErrorState = !canSubmit;

  return (
    <Container className={classNameVal} role="form" aria-labelledby="sms-subscription-container">
      <Row>
        <LabelContainer isTranslatedPage={isTranslatedPage}>
          <HeadingRow>
            {!isEmpty(heading1) ? (
              <HeadingLine1
                as="h2"
                id="sms-subscription-title"
                colorValue={textColorOverride}
                isTranslatedPage={isTranslatedPage}
              >
                {heading1.toUpperCase()}
              </HeadingLine1>
            ) : null}
            {!isEmpty(heading2) ? (
              <HeadingLine2
                as="h2"
                id="sms-subscription-title"
                colorValue={textColorOverride}
                isTranslatedPage={isTranslatedPage}
              >
                {heading2.toUpperCase()}
              </HeadingLine2>
            ) : null}
          </HeadingRow>
        </LabelContainer>
        <FormContainer as="form" onSubmit={onSubmit}>
          <FieldWrapper>
            <VisuallyHidden htmlFor="subscription-sms">{smsPlaceHolderText}</VisuallyHidden>
            <PhoneNumberInput
              id="subscription-sms"
              type="text"
              input={{
                value: phoneNumber,
                onChange: handleSMSChange,
                name: 'sms',
              }}
              placeholder={shouldShowPlaceholder ? smsPlaceHolderText.toUpperCase() : ''}
              aria-required="true"
              aria-label={smsPlaceHolderText.toUpperCase()}
              dataLocator="enter_sms_text_field"
              errorDataLocator="sms_error_message"
              enableSuccessCheck={false}
              normalize={formatPhoneNumber}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              autoComplete="off"
              onInput={(e) => {
                const onlyNumbers = e.target.value.replace(/\D/g, ''); // removes non-digits
                e.target.value = onlyNumbers;
              }}
            />
            {submissionMessage({
              showSuccess,
              isErrorState,
              submissionLabel,
              isSubscriptionError,
              subscribedErrorLabel,
              validationErrorLabel,
            })}
          </FieldWrapper>
          <SubmitButton
            type="submit"
            isSubmission={isErrorState || showSuccess}
            aria-label={submitTitle.toUpperCase()}
            disabled={!phoneNumber || isSubmitting || isErrorState}
          >
            {submitTitle.toUpperCase()}
          </SubmitButton>
        </FormContainer>
        <TabToJoinContainer>
          <a href={smsTemplate} className="anchor">
            <TabToJoinButton buttonVariation="variable-width" className="tapToJoin-cta">
              {tabToJoinCTA}
            </TabToJoinButton>
          </a>
        </TabToJoinContainer>
      </Row>
      {showDisclaimer ? (
        <DisclaimerRow>
          <RichText richTextHtml={smsDisclaimer.richText} />
        </DisclaimerRow>
      ) : null}
    </Container>
  );
};

SubscriptionSMSView.propTypes = {
  reset: PropTypes.func.isRequired,
  heading1: PropTypes.string.isRequired,
  heading2: PropTypes.string.isRequired,
  submitTitle: PropTypes.string.isRequired,
  classNameVal: PropTypes.string,
  textColorOverride: PropTypes.object,
  validateSignupEmail: PropTypes.func.isRequired,
  validationErrorLabel: PropTypes.string.isRequired,
  smsPlaceHolderText: PropTypes.string.isRequired,
};

SubscriptionSMSView.defaultProps = {
  classNameVal: '',
  textColorOverride: {},
};

export default SubscriptionSMSView;
